import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import TemuSyncRangeModal from '@/temu-platform/components/TemuSyncRangeModal';
import BatchQueryModal from '@/temu-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/temu-platform/modules/products/components/batchQueryUtils';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import TemuOpsListToolbar, { downloadCsv } from '@/temu-platform/components/TemuOpsListToolbar';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import HoverCopyField from '@/temu-platform/modules/products/components/HoverCopyField';
import { useGuardedRefresh } from '@/temu-platform/hooks/useGuardedRefresh';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { buildTemuPath } from '@/temu-platform/constants/temuRoutes';
import { useTemuPageShopScope, stackShopDimensionTabs } from '@/temu-platform/hooks/useTemuPageShopScope';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { extractTemuList, extractTemuTotal, pickField } from '@/temu-platform/utils/temuResponse';
import { formatSiteListSyncedAt } from '@/temu-platform/utils/temuSiteList';
import FinanceListGoodsCell, { extractGoodsList } from '@/temu-platform/modules/finance/FinanceListGoodsCell';
import TemuDateRangeFilter, {
  toDayEndDateTime,
  toDayStartDateTime,
  validateDateRange,
} from '@/temu-platform/components/TemuDateRangeFilter';
import {
  BILL_TYPE_CHECK,
  BILL_TYPE_REPORT,
  billPrimaryNo,
  CHECK_PAY_PAID,
  CHECK_PAY_TABS,
  CHECK_PAY_UNPAID,
  defaultBillTypeForMode,
  defaultFinancePayTimeType,
  FINANCE_IN_OUT_OPTIONS,
  FINANCE_SECOND_ORDER_TYPE_OPTIONS,
  financeInOutLabel,
  financePaymentMethodLabel,
  financePayTimeField,
  financeSecondOrderTypeLabel,
  financeTabLabel,
  financeTabsForMode,
  FINANCE_API_MODE_FULL,
  FINANCE_API_MODE_UNKNOWN,
  formatLedgerAmount,
  formatMoney,
  formatPayDate,
  resolveFinanceApiMode,
} from '@/temu-platform/modules/finance/financeDisplay';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-product-ops.css';

const PRODUCT_ID_OPTIONS = [
  { value: 'skc', label: 'SKC' },
  { value: 'sku', label: 'SKU' },
];

const EMPTY_FILTER = {
  billNo: '',
  incomeExpenditureType: '',
  secondOrderType: '',
  productIdType: 'skc',
  productIdValue: '',
  payTimeStart: '',
  payTimeEnd: '',
};

function rowKey(row, index, billType) {
  const id = row?.id ?? pickField(row, 'id');
  if (id != null && String(id).trim() !== '') {
    return `id:${id}`;
  }
  const primary = billPrimaryNo(row, billType);
  const bz = pickField(row, 'bzOrderNo') || '';
  const second = pickField(row, 'secondOrderType') || '';
  return `${primary}|${bz}|${second}|${index}`;
}

function toIntOrUndefined(raw) {
  if (raw == null || raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function buildFinanceListQuery(query, { isReport, checkPayStatus }) {
  const idType = query.productIdType === 'sku' ? 'sku' : 'skc';
  const idValue = !isReport && query.productIdValue ? query.productIdValue : undefined;
  const payTimeType = defaultFinancePayTimeType(isReport, checkPayStatus);
  return {
    source: 'cached',
    billType: isReport ? BILL_TYPE_REPORT : BILL_TYPE_CHECK,
    payStatus: isReport ? undefined : checkPayStatus,
    bzOrderNo: !isReport && query.billNo ? query.billNo : undefined,
    billNo: isReport && query.billNo ? query.billNo : undefined,
    reportOrderNo: isReport && query.billNo ? query.billNo : undefined,
    incomeExpenditureType: !isReport ? toIntOrUndefined(query.incomeExpenditureType) : undefined,
    secondOrderType: !isReport ? toIntOrUndefined(query.secondOrderType) : undefined,
    skuIdType: idValue ? idType : undefined,
    skuKeyword: idValue || undefined,
    payTimeStart: query.payTimeStart ? toDayStartDateTime(query.payTimeStart) : undefined,
    payTimeEnd: query.payTimeEnd ? toDayEndDateTime(query.payTimeEnd) : undefined,
    payTimeField: financePayTimeField(payTimeType),
  };
}

export default function BillListPage() {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const {
    activeShop,
    loading: shopLoading,
    shopDimensionTabs,
    concreteScopeId,
    allShopsScope,
  } = useTemuPageShopScope();
  const activeShopId = concreteScopeId;

  const financeApiMode = useMemo(
    () => resolveFinanceApiMode(activeShop?.shopType),
    [activeShop?.shopType],
  );
  const visibleTabs = useMemo(
    () => financeTabsForMode(financeApiMode),
    [financeApiMode],
  );

  const [billType, setBillType] = useState(BILL_TYPE_CHECK);
  const isReport = billType === BILL_TYPE_REPORT;
  const [checkPayStatus, setCheckPayStatus] = useState(CHECK_PAY_UNPAID);
  const isPaidCheck = !isReport && checkPayStatus === CHECK_PAY_PAID;
  const billNoLabel = isReport ? t('finance.reportNo') : t('finance.bizNo');
  const payTimeLabel = isReport || isPaidCheck ? t('finance.paidDate') : t('finance.estimatePay');

  const [filterDraft, setFilterDraft] = useState({ ...EMPTY_FILTER });
  const [query, setQuery] = useState({ ...EMPTY_FILTER, _nonce: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  /** 各维度数量：未选中的 Tab 也展示 */
  const [dimensionCounts, setDimensionCounts] = useState({
    checkUnpaid: 0,
    checkPaid: 0,
    report: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const selectedRowsRef = useRef(new Map());
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [batchModalField, setBatchModalField] = useState(null);
  const [syncRangeOpen, setSyncRangeOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const pendingSyncRangeRef = useRef(null);

  const { startJob, onTrackDone, isBusy } = useTemuSyncJobs();

  const listQueryParams = useMemo(
    () => buildFinanceListQuery(query, { isReport, checkPayStatus }),
    [checkPayStatus, isReport, query],
  );

  const fetchFinanceTotal = useCallback(async (params) => {
    if (!activeShopId) return 0;
    const res = await temuPlatformAPI.listFinanceBills(activeShopId, {
      page: 1,
      pageSize: 1,
      ...params,
    });
    return extractTemuTotal(res, 0);
  }, [activeShopId]);

  const loadDimensionCounts = useCallback(async () => {
    if (!activeShopId) {
      setDimensionCounts({ checkUnpaid: 0, checkPaid: 0, report: 0 });
      return;
    }
    const needReport = visibleTabs.some((t) => t.id === BILL_TYPE_REPORT);
    const needCheck = visibleTabs.some((t) => t.id === BILL_TYPE_CHECK);
    try {
      const [checkUnpaid, checkPaid, report] = await Promise.all([
        needCheck
          ? fetchFinanceTotal(buildFinanceListQuery(query, {
            isReport: false,
            checkPayStatus: CHECK_PAY_UNPAID,
          }))
          : Promise.resolve(0),
        needCheck
          ? fetchFinanceTotal(buildFinanceListQuery(query, {
            isReport: false,
            checkPayStatus: CHECK_PAY_PAID,
          }))
          : Promise.resolve(0),
        needReport
          ? fetchFinanceTotal(buildFinanceListQuery(query, {
            isReport: true,
            checkPayStatus: CHECK_PAY_UNPAID,
          }))
          : Promise.resolve(0),
      ]);
      setDimensionCounts({
        checkUnpaid: Number(checkUnpaid) || 0,
        checkPaid: Number(checkPaid) || 0,
        report: Number(report) || 0,
      });
    } catch {
      /* 维度数量失败不阻断列表 */
    }
  }, [activeShopId, fetchFinanceTotal, query, visibleTabs]);

  const run = useCallback(async () => {
    if (!activeShopId) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await temuPlatformAPI.listFinanceBills(activeShopId, {
        page,
        pageSize,
        ...listQueryParams,
      });
      const list = extractTemuList(res);
      const nextTotal = extractTemuTotal(res, list.length);
      setRows(list);
      setTotal(nextTotal);
      setDimensionCounts((prev) => {
        if (isReport) return { ...prev, report: nextTotal };
        if (checkPayStatus === CHECK_PAY_PAID) return { ...prev, checkPaid: nextTotal };
        return { ...prev, checkUnpaid: nextTotal };
      });
    } catch (e) {
      setRows([]);
      setTotal(0);
      setError(e?.message || t('finance.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeShopId, checkPayStatus, isReport, listQueryParams, page, pageSize, query._nonce, t]);

  const updateFilterField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (shopLoading) return;
    run();
  }, [run, shopLoading]);

  useEffect(() => {
    if (shopLoading) return;
    loadDimensionCounts();
  }, [loadDimensionCounts, shopLoading]);

  useEffect(() => {
    setPage(1);
    setSelectedKeys(new Set());
    setFilterDraft((prev) => ({
      ...prev,
      payTimeStart: '',
      payTimeEnd: '',
    }));
    setQuery((prev) => ({
      ...prev,
      payTimeStart: '',
      payTimeEnd: '',
      _nonce: prev._nonce + 1,
    }));
  }, [billType, checkPayStatus, activeShopId]);

  // 筛选查询变化时清空跨页选中（不含翻页）
  const filterNonce = query._nonce;
  useEffect(() => {
    setSelectedKeys(new Set());
  }, [filterNonce]);

  useEffect(() => {
    const nextDefault = defaultBillTypeForMode(financeApiMode);
    const allowed = visibleTabs.some((t) => t.id === billType);
    if (!allowed) {
      setBillType(nextDefault);
    }
  }, [financeApiMode, visibleTabs, billType]);

  useEffect(() => {
    if (!activeShopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getShopSyncStatus(activeShopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const mod = modules.find((m) => String(m.module || '').toUpperCase() === 'FINANCE');
        const at = mod?.lastSyncEndAt || mod?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [activeShopId, lastSyncedAt]);

  const handleSync = useCallback(async () => {
    const range = pendingSyncRangeRef.current;
    if (!activeShopId || !range?.startTime || !range?.endTime) {
      showToast(t('finance.pickRange'), 'warning');
      return;
    }
    if (financeApiMode === FINANCE_API_MODE_UNKNOWN) {
      showToast(
        t('finance.modeMismatch', { shopType: activeShop?.shopType || t('finance.shopTypeUnset') }),
        'error',
      );
      return;
    }
    try {
      const track = await startJob({
        kind: 'finance',
        shopId: activeShopId,
        label: t('publishRecord.syncUpdate'),
        payload: {
          startAddTime: range.startTime,
          endAddTime: range.endTime,
        },
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      const finishedAt = new Date().toISOString();
      setLastSyncedAt(finishedAt);
      setPersistedSyncedAt(finishedAt);
      pendingSyncRangeRef.current = null;
      setPage(1);
      setQuery((prev) => ({ ...prev, _nonce: prev._nonce + 1 }));
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') {
        return;
      }
      showToast(e?.message || t('productAction.syncFailed'), 'error');
    }
  }, [activeShop?.shopType, activeShopId, financeApiMode, startJob, onTrackDone, t]);

  const {
    refresh: guardedSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync);

  const syncing = syncingLocal || isBusy('finance', activeShopId);

  const productIdLabel = PRODUCT_ID_OPTIONS.find((o) => o.value === filterDraft.productIdType)?.label || 'SKC';

  const onQuery = () => {
    const tokens = parseBatchTokens(filterDraft.billNo).slice(0, BATCH_QUERY_LIMIT);
    const productTokens = parseBatchTokens(filterDraft.productIdValue).slice(0, BATCH_QUERY_LIMIT);
    const rangeError = validateDateRange(filterDraft.payTimeStart, filterDraft.payTimeEnd);
    if (rangeError) {
      showToast(rangeError, 'warning');
      return;
    }
    if (activeShopId) setLoading(true);
    setPage(1);
    setQuery((prev) => ({
      ...EMPTY_FILTER,
      billNo: tokens.join(','),
      incomeExpenditureType: filterDraft.incomeExpenditureType || '',
      secondOrderType: filterDraft.secondOrderType || '',
      productIdType: filterDraft.productIdType === 'sku' ? 'sku' : 'skc',
      productIdValue: productTokens.join(','),
      payTimeStart: filterDraft.payTimeStart || '',
      payTimeEnd: filterDraft.payTimeEnd || '',
      _nonce: prev._nonce + 1,
    }));
  };

  const onReset = () => {
    setFilterDraft({ ...EMPTY_FILTER });
    setPage(1);
    setQuery((prev) => ({
      ...EMPTY_FILTER,
      _nonce: prev._nonce + 1,
    }));
  };

  const openDetail = (row) => {
    const no = billPrimaryNo(row, billType);
    if (!no) return;
    navigate(buildTemuPath(`finance/${billType}/${encodeURIComponent(no)}`));
  };

  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(latestSyncedAt) })
    : t('tiktokCatalog.notSynced');

  const emptyTitle = !activeShopId
    ? t('returnList.pickShop')
    : loading
      ? t('returnList.loading')
      : (isReport
        ? t('finance.emptyReport')
        : (isPaidCheck ? t('finance.emptyPaid') : t('finance.emptyUnpaid')));

  const emptyDesc = !activeShopId || loading
    ? null
    : t('finance.emptyPull', { platform: 'TEMU' });

  const selectableKeys = useMemo(
    () => rows.map((row, index) => rowKey(row, index, billType)),
    [rows, billType],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedRows = useMemo(() => {
    const map = selectedRowsRef.current;
    for (const key of [...map.keys()]) {
      if (!selectedKeys.has(key)) map.delete(key);
    }
    rows.forEach((row, index) => {
      const key = rowKey(row, index, billType);
      if (selectedKeys.has(key)) map.set(key, row);
    });
    return Array.from(selectedKeys)
      .map((key) => map.get(key))
      .filter(Boolean);
  }, [rows, selectedKeys, billType]);


  const buildExportRows = (list) => {
    if (isReport) {
      const headers = [
        'reportOrderNo',
        'rxAcct',
        'completedPayTime',
        'paymentMethod',
        'amount',
        'currencyCode',
      ];
      const csvRows = (Array.isArray(list) ? list : []).map((row) => ({
        reportOrderNo: billPrimaryNo(row, BILL_TYPE_REPORT),
        rxAcct: pickField(row, 'rxAcct') || '',
        completedPayTime: pickField(row, 'completedPayTime') || '',
        paymentMethod: financePaymentMethodLabel(pickField(row, 'paymentMethod')),
        amount: pickField(row, 'amount', 'income', 'estimateIncomeMoneyTotal'),
        currencyCode: pickField(row, 'currencyCode'),
      }));
      return { headers, csvRows };
    }
    const headers = [
      'platformOrderNo',
      'goodsTitles',
      'secondOrderType',
      'incomeExpenditureType',
      isPaidCheck ? 'completedPayTime' : 'estimatePayTime',
      'amount',
      'currencyCode',
    ];
    const csvRows = (Array.isArray(list) ? list : []).map((row) => {
      const goods = extractGoodsList(row);
      return {
        platformOrderNo: pickField(row, 'bzOrderNo') || '',
        goodsTitles: goods
          .map((g) => {
            const title = pickField(g, 'goodsTitle', 'productName', 'title') || '';
            const merchantSku = pickField(g, 'supplierSku', 'sellerSku', 'merchantSku') || '';
            if (title && merchantSku) return `${title}(${merchantSku})`;
            return title || merchantSku || '';
          })
          .filter(Boolean)
          .join(' | '),
        secondOrderType: financeSecondOrderTypeLabel(pickField(row, 'secondOrderType')),
        incomeExpenditureType: financeInOutLabel(pickField(row, 'incomeExpenditureType')),
        ...(isPaidCheck
          ? { completedPayTime: pickField(row, 'completedPayTime') || '' }
          : { estimatePayTime: pickField(row, 'estimatePayTime') || '' }),
        amount: pickField(row, 'amount', 'estimateIncomeMoneyTotal'),
        currencyCode: pickField(row, 'currencyCode'),
      };
    });
    return { headers, csvRows };
  };

  const exportRows = (list, emptyMessage) => {
    if (!list.length) {
      showToast(emptyMessage, 'warning');
      return;
    }
    const { headers, csvRows } = buildExportRows(list);
    const prefix = isReport ? 'temu-finance-settlements' : 'temu-finance-ledgers';
    downloadCsv(`${prefix}-${Date.now()}.csv`, headers, csvRows);
    showToast(t('tiktokCatalog.exported', { count: csvRows.length }), 'success');
  };

  const exportSelected = () => {
    exportRows(selectedRows, t('finance.pickExport', { name: financeTabLabel(billType) }));
  };

  const exportPage = () => {
    exportRows(rows, t('finance.noneExport', { name: financeTabLabel(billType) }));
  };

  const exportFiltered = async () => {
    if (!activeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    if (total <= 0 && rows.length === 0) {
      showToast(t('finance.noneExport', { name: financeTabLabel(billType) }), 'warning');
      return;
    }
    setExporting(true);
    try {
      const exportPageSize = 100;
      let pageNo = 1;
      let expectedTotal = Number.isFinite(total) && total > 0 ? total : Infinity;
      const all = [];
      while (all.length < expectedTotal) {
        const res = await temuPlatformAPI.listFinanceBills(activeShopId, {
          page: pageNo,
          pageSize: exportPageSize,
          ...listQueryParams,
        });
        const list = extractTemuList(res);
        expectedTotal = extractTemuTotal(res, list.length);
        all.push(...list);
        if (!list.length || list.length < exportPageSize) break;
        pageNo += 1;
        if (pageNo > 500) break;
      }
      exportRows(all, t('finance.noneExport', { name: financeTabLabel(billType) }));
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const typeTabsInner = (
    <div className="tpo-finance-tabs">
      <div className="tpo-status-tabs" role="tablist" aria-label={t('finance.billType')}>
        {visibleTabs.map((tab) => {
          const active = billType === tab.id;
          const count = tab.id === BILL_TYPE_REPORT
            ? (Number(dimensionCounts.report) || 0)
            : ((Number(dimensionCounts.checkUnpaid) || 0) + (Number(dimensionCounts.checkPaid) || 0));
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`tpo-status-tab${active ? ' tpo-status-tab--active' : ''}`}
              onClick={() => setBillType(tab.id)}
            >
              {t(tab.labelKey)}
              <span className="tpo-status-tab__count">{count}</span>
            </button>
          );
        })}
      </div>
      {!isReport ? (
        <div className="tpo-status-tabs tpo-finance-pay-tabs" role="tablist" aria-label={t('finance.payAria')}>
          {CHECK_PAY_TABS.map((tab) => {
            const active = checkPayStatus === tab.id;
            const count = tab.id === CHECK_PAY_PAID
              ? (Number(dimensionCounts.checkPaid) || 0)
              : (Number(dimensionCounts.checkUnpaid) || 0);
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`tpo-status-tab tpo-status-tab--sub${active ? ' tpo-status-tab--active' : ''}`}
                onClick={() => setCheckPayStatus(tab.id)}
              >
                {t(tab.labelKey)}
                <span className="tpo-status-tab__count">{count}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
  const typeTabs = stackShopDimensionTabs(shopDimensionTabs, typeTabsInner);

  const colSpan = 7;

  const onBatchDelete = async () => {
    if (!activeShopId || selectedKeys.size === 0) {
      showToast(t('finance.pickDelete'), 'warning');
      return;
    }
    const ids = selectedRows
      .map((row) => Number(row?.id ?? pickField(row, 'id')))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length === 0) {
      showToast(t('finance.missingId', { platform: 'TEMU' }), 'error');
      return;
    }
    const ok = await showTemuConfirm({
      title: t('returnList.batchDelete'),
      message: t('finance.deleteMsg', { count: ids.length, platform: 'TEMU' }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await temuPlatformAPI.batchDeleteFinanceBills(activeShopId, ids);
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : ids.length;
      if (count > 0) {
        showToast(t('finance.deletedCache', { count }), 'success');
      } else {
        showToast(t('certList.noneDeletedCache'), 'warning');
      }
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      await run();
      await loadDimensionCounts();
    } catch (e) {
      showToast(e?.message || t('shop.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <TemuSyncProgressSlot kinds={['finance']} />
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || exporting || deleting}
            syncKinds={['finance']}
            onSync={() => {
              if (!activeShopId) {
                showToast(t('publishRecord.pickAuthorized', { platform: 'TEMU' }), 'warning');
                return;
              }
              setSyncRangeOpen(true);
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={shopLoading || !activeShopId || syncing}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('finance.syncHint', { platform: 'TEMU' })}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={exporting || !activeShopId}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!activeShopId || deleting}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            {isReport ? (
              <div className="tpo-field tpo-field--inline tpo-field--batch tpo-field--w300">
                <span className="tpo-field__label">{billNoLabel}</span>
                <input
                  className="tpo-input"
                  value={filterDraft.billNo}
                  placeholder={t('tiktokCatalog.idPlaceholder', { label: billNoLabel })}
                  disabled={!activeShopId}
                  onChange={(e) => updateFilterField('billNo', e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                />
                <button
                  type="button"
                  className="tpo-batch-add-btn"
                  aria-label={t('tiktokCatalog.batchInput', { label: billNoLabel })}
                  title={t('tiktokCatalog.batchQuery')}
                  disabled={!activeShopId}
                  onClick={() => setBatchModalField('billNo')}
                >
                  <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
                </button>
              </div>
            ) : (
              <>
                <div className="tpo-field tpo-field--inline tpo-field--status">
                  <span className="tpo-field__label">{t('finance.inOut')}</span>
                  <select
                    className="tpo-select tpo-select--status"
                    value={filterDraft.incomeExpenditureType}
                    disabled={!activeShopId}
                    aria-label={t('finance.inOut')}
                    onChange={(e) => updateFilterField('incomeExpenditureType', e.target.value)}
                  >
                    <option value="">{t('tiktokCatalog.all')}</option>
                    {FINANCE_IN_OUT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{financeInOutLabel(opt.value)}</option>
                    ))}
                  </select>
                </div>
                <div className="tpo-field tpo-field--inline tpo-field--w220">
                  <span className="tpo-field__label">{t('finance.billType')}</span>
                  <select
                    className="tpo-select"
                    value={filterDraft.secondOrderType}
                    disabled={!activeShopId}
                    aria-label={t('finance.billType')}
                    onChange={(e) => updateFilterField('secondOrderType', e.target.value)}
                  >
                    <option value="">{t('certLib.allTypes')}</option>
                    {FINANCE_SECOND_ORDER_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{financeSecondOrderTypeLabel(opt.value)}</option>
                    ))}
                  </select>
                </div>
                <div className="tpo-field tpo-field--inline tpo-field--id-combo tpo-field--batch tpo-field--w300">
                  <select
                    className="tpo-select tpo-select--compact"
                    value={filterDraft.productIdType}
                    disabled={!activeShopId}
                    aria-label={t('finance.idType')}
                    onChange={(e) => updateFilterField('productIdType', e.target.value)}
                  >
                    {PRODUCT_ID_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <input
                    className="tpo-input"
                    value={filterDraft.productIdValue}
                    placeholder={t('tiktokCatalog.idPlaceholder', { label: productIdLabel })}
                    disabled={!activeShopId}
                    onChange={(e) => updateFilterField('productIdValue', e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                  />
                  <button
                    type="button"
                    className="tpo-batch-add-btn"
                    aria-label={t('tiktokCatalog.batchInput', { label: productIdLabel })}
                    title={t('tiktokCatalog.batchQuery')}
                    disabled={!activeShopId}
                    onClick={() => setBatchModalField('productIdValue')}
                  >
                    <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
                  </button>
                </div>
                <div className="tpo-field tpo-field--inline tpo-field--batch tpo-field--w300">
                  <span className="tpo-field__label">{billNoLabel}</span>
                  <input
                    className="tpo-input"
                    value={filterDraft.billNo}
                    placeholder={t('tiktokCatalog.idPlaceholder', { label: billNoLabel })}
                    disabled={!activeShopId}
                    onChange={(e) => updateFilterField('billNo', e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                  />
                  <button
                    type="button"
                    className="tpo-batch-add-btn"
                    aria-label={t('tiktokCatalog.batchInput', { label: billNoLabel })}
                    title={t('tiktokCatalog.batchQuery')}
                    disabled={!activeShopId}
                    onClick={() => setBatchModalField('billNo')}
                  >
                    <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
                  </button>
                </div>
              </>
            )}
            <TemuDateRangeFilter
              showTimeType={false}
              timeTypeLabel={payTimeLabel}
              startDate={filterDraft.payTimeStart}
              endDate={filterDraft.payTimeEnd}
              disabled={!activeShopId}
              timeTypeAriaLabel={payTimeLabel}
              onStartDateChange={(v) => updateFilterField('payTimeStart', v)}
              onEndDateChange={(v) => updateFilterField('payTimeEnd', v)}
            />
          </div>

          <div className="tpo-filter-bar__actions">
            <button
              type="button"
              className="tpo-btn tpo-btn--secondary tpo-btn--sm"
              disabled={!activeShopId}
              onClick={onReset}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
              disabled={!activeShopId || loading}
              onClick={onQuery}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
        {error ? (
          <div className="tpo-ops-error" role="alert">{error}</div>
        ) : null}
      </ProductFilterCard>

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={t('finance.batchQueryNamed', { label: batchModalField === 'productIdValue' ? productIdLabel : billNoLabel })}
        initialValue={
          batchModalField === 'productIdValue'
            ? filterDraft.productIdValue
            : filterDraft.billNo
        }
        placeholder={t('orderList.idLinePh', { type: batchModalField === 'productIdValue' ? productIdLabel : billNoLabel })}
        onConfirm={(tokens) => {
          const joined = joinBatchTokens(tokens);
          if (batchModalField === 'productIdValue') {
            setFilterDraft((prev) => ({ ...prev, productIdValue: joined }));
          } else {
            setFilterDraft((prev) => ({ ...prev, billNo: joined }));
          }
        }}
        onClose={() => setBatchModalField(null)}
      />

      <TemuSyncRangeModal
        open={syncRangeOpen}
        title={t('finance.syncTitle')}
        defaultHours={24 * 7}
        presets={[
          { label: t('orderList.last24h'), hours: 24 },
          { label: t('orderList.last7d'), hours: 24 * 7 },
          { label: t('orderList.last14d'), hours: 24 * 14 },
          { label: t('orderList.last30d'), hours: 24 * 30 },
          { label: t('orderList.last90d'), hours: 24 * 90 },
        ]}
        confirming={syncing}
        onConfirm={async (range) => {
          pendingSyncRangeRef.current = range;
          const ok = await guardedSync();
          if (ok !== false) setSyncRangeOpen(false);
        }}
        onClose={() => {
          if (!syncing) setSyncRangeOpen(false);
        }}
      />

      <ProductListCard tabs={typeTabs} loading={Boolean(activeShopId) && loading} loadingLabel={t('finance.querying')}>
        <div className="tpo-drafts__table-wrap tpo-ops-table-wrap tpo-finance-list__table-wrap">
          <table className={`tpo-publish__table tpo-drafts__table tpo-ops-table tpo-finance-list__table${isReport ? ' tpo-finance-list__table--report' : ''}`}>
            <colgroup>
              <col className="tpo-ops-col tpo-ops-col--check temu-product-col--check" />
              {isReport ? (
                <>
                  <col />
                  <col />
                  <col />
                  <col />
                  <col className="tpo-ops-col tpo-ops-col--absorb" />
                  <col className="tpo-ops-col tpo-ops-col--ops" />
                </>
              ) : (
                <>
                  <col />
                  <col />
                  <col />
                  <col />
                  <col />
                  <col className="tpo-ops-col tpo-ops-col--absorb" />
                </>
              )}
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tpo-ops-th tpo-ops-th--check temu-product-cell--spu-check"
                  onClick={() => {
                    if (!activeShopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('finance.selectAll', { name: financeTabLabel(billType) })}
                    disabled={!activeShopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                {isReport ? (
                  <>
                    <th scope="col">{t('finance.reportNo')}</th>
                    <th scope="col">{t('finance.account')}</th>
                    <th scope="col">{t('finance.paidDate')}</th>
                    <th scope="col">{t('finance.payMethod')}</th>
                    <th scope="col">{t('finance.settledAmt')}</th>
                    <th scope="col" className="tpo-ops-th--ops">{t('certLib.colOps')}</th>
                  </>
                ) : (
                  <>
                    <th scope="col">{t('orderList.productInfo')}</th>
                    <th scope="col">{t('finance.bizNo')}</th>
                    <th scope="col">{t('finance.billType')}</th>
                    <th scope="col">{t('finance.inOut')}</th>
                    <th scope="col">{payTimeLabel}</th>
                    <th scope="col">{t('finance.receivable')}</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {!activeShopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="tpo-certs-empty-cell">
                    <div className="tpo-certs-empty">
                      <p className="tpo-certs-empty__title">{emptyTitle}</p>
                      {emptyDesc ? <p className="tpo-certs-empty__desc">{emptyDesc}</p> : null}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const key = rowKey(row, index, billType);
                  const primaryNo = billPrimaryNo(row, billType);
                  if (isReport) {
                    const settled = formatMoney(
                      pickField(row, 'amount', 'income', 'estimateIncomeMoneyTotal'),
                      pickField(row, 'currencyCode'),
                    );
                    return (
                      <tr key={key}>
                        <td
                          className="tpo-ops-td tpo-ops-td--check temu-product-cell--spu-check"
                          onClick={() => toggleOne(key)}
                        >
                          <input
                            type="checkbox"
                            aria-label={t('orderList.selectRow', { title: primaryNo || key })}
                            checked={selectedKeys.has(key)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleOne(key)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="tpo-finance-link"
                            disabled={!primaryNo}
                            onClick={() => openDetail(row)}
                          >
                            {primaryNo || '—'}
                          </button>
                        </td>
                        <td>{pickField(row, 'rxAcct') || '—'}</td>
                        <td>{formatPayDate(pickField(row, 'completedPayTime'))}</td>
                        <td>{financePaymentMethodLabel(pickField(row, 'paymentMethod'))}</td>
                        <td className="tpo-finance-amt">{settled}</td>
                        <td className="tpo-certs-td tpo-certs-td--ops tpo-ops-td--ops">
                          <div className="tpo-drafts__actions">
                            <button
                              type="button"
                              className="tpo-btn tpo-btn--primary tpo-btn--sm"
                              disabled={!primaryNo}
                              onClick={() => openDetail(row)}
                            >
                              {t('finance.viewDetail')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }

                  const ledgerAmt = formatLedgerAmount(
                    pickField(row, 'amount', 'estimateIncomeMoneyTotal'),
                    pickField(row, 'currencyCode'),
                    pickField(row, 'incomeExpenditureType'),
                  );
                  const inOut = financeInOutLabel(pickField(row, 'incomeExpenditureType'));
                  const goods = extractGoodsList(row);
                  const bzOrderNo = pickField(row, 'bzOrderNo') || '';
                  return (
                    <tr key={key}>
                      <td
                        className="tpo-ops-td tpo-ops-td--check temu-product-cell--spu-check"
                        onClick={() => toggleOne(key)}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('orderList.selectRow', { title: primaryNo || key })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key)}
                        />
                      </td>
                      <td className="tpo-ops-td tpo-ops-td--product">
                        <FinanceListGoodsCell goods={goods} />
                      </td>
                      <td className="tpo-ops-td tpo-ops-td--orderno">
                        <div className="tpo-finance-bz-cell">
                          <HoverCopyField
                            className="tpo-return-copy"
                            value={bzOrderNo}
                            display={bzOrderNo ? <strong>{bzOrderNo}</strong> : '—'}
                            emptyText="—"
                          />
                          {goods.length > 0 ? (
                            <div className="tpo-finance-bz-cell__count">{t('finance.orderCount', { count: goods.length })}</div>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <span className="tpo-finance-bill-type">
                          {financeSecondOrderTypeLabel(pickField(row, 'secondOrderType'))}
                        </span>
                      </td>
                      <td>
                        <span className={`tpo-finance-inout tpo-finance-inout--${Number(pickField(row, 'incomeExpenditureType')) === 2 ? 'out' : 'in'}`}>
                          {inOut}
                        </span>
                      </td>
                      <td>
                        {formatPayDate(
                          isPaidCheck
                            ? pickField(row, 'completedPayTime')
                            : pickField(row, 'estimatePayTime'),
                        )}
                      </td>
                      <td className={`tpo-finance-amt tpo-finance-amt--${ledgerAmt.tone}`}>
                        {ledgerAmt.text}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <TemuOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!activeShopId}
          pageItemCount={rows.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('finance.pager')}
        />
      </ProductListCard>
    </>
  );
}
